import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import { loadParentEmailThreads } from "../store/actions";
import {
  canLoadParentEmailThreads,
  getParentEmailThreads,
  isParentEmailThreadsLoading,
  isParentEmailThreadsSearching,
} from "../store/selectors";

import messages from "./messages";
import ParentEmail from "./ParentEmail";
import styles from "./ParentEmailsList.module.scss";

interface ParentEmailsListProps {
  canLoadEmailThreads: boolean;
  emailThreads: EmailThread[];
  emailThreadsLoading: boolean;
  emailThreadsSearching: boolean;
  onLoadEmailThreads: typeof loadParentEmailThreads.request;
}

const ParentEmailsList: React.FC<ParentEmailsListProps> = ({
  canLoadEmailThreads,
  emailThreads,
  emailThreadsLoading,
  emailThreadsSearching,
  onLoadEmailThreads,
}) => {
  const intl = useIntl();
  const loaderRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (
        entry.isIntersecting &&
        canLoadEmailThreads &&
        !emailThreadsLoading &&
        !emailThreadsSearching
      ) {
        onLoadEmailThreads();
      }
    });
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    return () => observer.disconnect();
  }, [
    canLoadEmailThreads,
    emailThreadsLoading,
    emailThreadsSearching,
    onLoadEmailThreads,
    loaderRef,
  ]);

  if (emailThreadsSearching) {
    return <LoadingSpinner className={styles.loadingSpinner} />;
  }

  if (!emailThreads.length) {
    return (
      <div className={styles.message}>{intl.formatMessage(messages.emailsNotFoundMessage)}</div>
    );
  }

  return (
    <div className={styles.container}>
      {emailThreads.map((emailThread) => (
        <div key={emailThread.id}>
          <ParentEmail emailThread={emailThread} />
        </div>
      ))}
      {emailThreadsLoading && <LoadingSpinner className={styles.loadingSpinner} />}
      <div className={styles.loader} ref={loaderRef} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canLoadEmailThreads: canLoadParentEmailThreads(state),
  emailThreads: getParentEmailThreads(state),
  emailThreadsLoading: isParentEmailThreadsLoading(state),
  emailThreadsSearching: isParentEmailThreadsSearching(state),
});

const mapDispatchToProps = {
  onLoadEmailThreads: loadParentEmailThreads.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ParentEmailsList);
